// BCM-898 · PA6 — Produced Qty Variance (slide 22-23).
// Rendered as its OWN stacked section, independent of the "Consumed Batch
// Variance/Mismatch" tab, only when producedQtyVariancePresent is true and the
// applicable direction's BCM-744 update type requires action. Unlike the Stat
// Date section, this DOES carry a second "View Document" tab — the produced
// quantity is digitalized from the same document that carries this batch's
// consumption bowls (named differently per CMO — "Certificate of Manufacture",
// "Confirmation of the Partial Manufacturing of a Medicinal Product",
// "Material Accountability Report", …), so Sync Inventory / Sync PO / Resubmit
// Document all apply here too. The document tab reuses ConsumptionDocumentTab
// (same as PA4/PA6's own "View Consumption Document" tab) — its onlyConsumption
// (hasBowls) filter finds the right document by STRUCTURE regardless of what
// it's named, instead of matching one hardcoded document_type string that would
// only ever work for a single CMO's naming convention (BCM-898 bug fix).
//
// Multiple Variance/Mismatch Scenario (design slides 24-25): when more than
// one variance section is present, they stack one below the other and share
// ONE common Approve button, rendered as its own block below ALL the sections
// (by PA6VarianceMismatch) — not nested inside any single section's "Action
// Required" box. So this component only renders the read-only/editable
// fields; the edited qty lives in PA6VarianceMismatch (value/onChange) and
// Approve/validation/payload/toast live there too. onActiveTabChange reports
// which of this section's own two tabs is active, so the parent's shared
// Approve button can hide itself while "View Document" is open — approving
// doesn't apply while reviewing the underlying document.
import React from "react";
import {
  Typography, Box, TextField,
} from "@material-ui/core";
import WarningRoundedIcon from "@material-ui/icons/WarningRounded";
import ActionTabs from "../../components/common/ActionTabs";
import SyncActions from "../../components/common/SyncActions";
import ResubmitDocumentButton from "../../components/common/ResubmitDocumentButton";
import ConsumptionDocumentTab from "../../components/common/ConsumptionDocumentTab";
import { useGRStyles } from "../../goodsReceiptStyles";
import { trimQty } from "../../utils/formatters";

const InfoField = ({ classes, label, value, orange }) => (
  <div>
    <div className={classes.fieldLabel}>{label}</div>
    <div className={classes.fieldValue} style={orange ? { color: "#E36E39" } : undefined}>{value == null || value === "" ? "-" : value}</div>
  </div>
);

// Fixed 3-column grid so the two rows line up exactly, with a divider between
// them — matches the mockup's info card precisely.
const infoGridStyle = { display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: "12px 16px" };

export const isMoreQtyVariance = (row) => (row?.producedQuantity ?? 0) > (row?.targetQuantity ?? 0);

// Gate used by PA6VarianceMismatch to decide whether to mount this section.
export const shouldShowProducedQtyVariance = (row) => {
  if (!row || row.producedQtyVariancePresent !== true) return false;
  const more = isMoreQtyVariance(row);
  return more
    ? row.moreProducedQtyUpdateType === "Manual" || row.moreProducedQtyUpdateType === "Automatic after approval"
    : row.lessProducedQtyUpdateType === "Manual" || row.lessProducedQtyUpdateType === "Automatic after approval";
};

export const isProducedQtyEditable = (row) => {
  const more = isMoreQtyVariance(row);
  return more ? row.moreProducedQtyUpdateType === "Manual" : row.lessProducedQtyUpdateType === "Manual";
};

export const isProducedQtyAutoAfterApproval = (row) => {
  const more = isMoreQtyVariance(row);
  return more
    ? row.moreProducedQtyUpdateType === "Automatic after approval"
    : row.lessProducedQtyUpdateType === "Automatic after approval";
};

const ProducedQtyVarianceSection = ({
  row, detail, featuresAccess, viewOnly, onRefresh, value, onChange, onActiveTabChange,
}) => {
  const classes = useGRStyles();
  const more = isMoreQtyVariance(row);
  const editable = isProducedQtyEditable(row);

  const variancePct = row.targetQuantity > 0
    ? Math.abs((((row.producedQuantity || 0) - row.targetQuantity) / row.targetQuantity) * 100).toFixed(3)
    : "0.000";

  const varianceTab = () => (
    <React.Fragment>
      <Box display="flex" alignItems="center" style={{ gap: 6, marginBottom: 8 }}>
        <span style={{ color: "#C0392B", fontWeight: 700, display: "inline-flex", alignItems: "center", gap: 4, border: "1px solid #C0392B", borderRadius: 12, padding: "2px 10px", fontSize: "0.75rem" }}>
          <WarningRoundedIcon style={{ fontSize: 16 }} />
          {variancePct}% {more ? "OVER" : "UNDER"} PO QTY
        </span>
      </Box>
      <Typography className={classes.sectionSub} style={{ marginBottom: 12 }}>
        Produced quantity {more ? "exceeds" : "falls short of"} the PO quantity beyond the allowed variance. Update the SAP PO quantity and approve to clear the variance.
      </Typography>

      <Box display="flex" style={{ gap: 16, alignItems: "stretch" }} flexWrap="wrap">
        {/* BMS Product Code / Description — read-only context, matches the mockup's left card. */}
        <Box flex="1 1 320px" style={{ border: "1px solid #E0E4EA", borderRadius: 8, padding: "14px 18px" }}>
          <Typography style={{ fontWeight: 700, fontSize: "0.85rem", marginBottom: 12 }}>
            {row.fgMaterialCode} / {row.fgMaterialName}
          </Typography>
          <Box style={infoGridStyle}>
            <InfoField classes={classes} label="UOM" value={row.batchUOM} />
            <InfoField classes={classes} label="PO Qty" value={trimQty(row.targetQuantity)} />
            <InfoField classes={classes} label="Produced Qty" value={trimQty(row.producedQuantity)} />
          </Box>
          <Box style={{ borderTop: "1px solid #E0E4EA", margin: "12px 0" }} />
          <Box style={infoGridStyle}>
            {/* Design slide 22: both limits always show, regardless of direction —
                only the "Update SAP PO Qty To" field varies (editable vs read-only)
                based on the BCM-744 config for whichever direction applies. */}
            <InfoField classes={classes} label="Less Variance Limit" value={`${row.lessProducedQtyVarianceLimitPercentage ?? "-"}%`} />
            <InfoField classes={classes} label="More Variance Limit" value={`${row.moreProducedQtyVarianceLimitPercentage ?? "-"}%`} />
            <InfoField classes={classes} label="Produced Qty Variance" value={`${variancePct}%`} orange />
          </Box>
        </Box>

        {/* Action Required — the editable/read-only qty. The Approve button
            is NOT rendered here — it's a separate, common block below every
            stacked section (see PA6VarianceMismatch). */}
        <Box flex="1 1 260px" style={{ border: "1px solid #E36E39", borderRadius: 8, padding: "14px 18px" }}>
          <Typography style={{ fontWeight: 700, fontSize: "0.85rem", marginBottom: 12 }}>Action Required</Typography>
          <div className={classes.fieldLabel} style={{ marginBottom: 6 }}>
            Update SAP PO Qty To{editable ? <span style={{ color: "red" }}> *</span> : ""}
          </div>
          {editable ? (
            <TextField
              type="number"
              size="small"
              variant="outlined"
              value={value}
              onChange={(e) => onChange(e.target.value)}
              style={{ width: 140 }}
            />
          ) : (
            <Typography style={{ fontWeight: 700 }}>{trimQty(row.producedQuantity)}</Typography>
          )}
          <Typography variant="caption" style={{ display: "block", color: "#6B7280", marginTop: 6, fontStyle: "italic" }}>
            Approving writes this quantity back to the PO in SAP and clears the variance.
          </Typography>
        </Box>
      </Box>
    </React.Fragment>
  );

  return (
    <ActionTabs
      onActiveChange={onActiveTabChange}
      headRight={
        <React.Fragment>
          <SyncActions row={row} detail={detail} showSyncInventory showSyncPO resetsBatchPO resetsBatchInventory onRefresh={onRefresh} />
          <ResubmitDocumentButton row={row} detail={detail} featuresAccess={featuresAccess} onAfter={onRefresh} blue />
        </React.Fragment>
      }
      tabs={[
        { key: "producedqty", label: "Produced Qty Variance", badge: 1, render: varianceTab },
        {
          key: "doc",
          label: "View Document",
          render: () => (
            // BCM-898 bug fix: the document that carries producedQuantity /
            // date_of_manufacture is NOT named consistently across CMOs — real
            // batches have shown it as "Certificate of Manufacture" (Patheon),
            // "Confirmation of the Partial Manufacturing of a Medicinal
            // Product" (PCI Ireland), and "Material Accountability Report"
            // (others) — so matching a single hardcoded onlyDocType string can
            // never work for every CMO; it only ever matched one vendor's name
            // and fell through to "No digitalized documents are available for
            // this batch yet." for every other one. Every one of those
            // documents carries the same `bowls` (consumption) data, so
            // ConsumptionDocumentTab's onlyConsumption (hasBowls) filter finds
            // the right document regardless of what it's named — exactly the
            // same, CMO-agnostic match PA4/PA6's own "View Consumption
            // Document" tab already relies on (and its own comment already
            // names this as its second intended use: "the produced-qty
            // document tab, slide 21").
            <ConsumptionDocumentTab
              row={row}
              detail={detail}
              onRefresh={onRefresh}
              viewOnly={viewOnly}
            />
          ),
        },
      ]}
    />
  );
};

export default ProducedQtyVarianceSection;
